import Anthropic from "@anthropic-ai/sdk";
import { zodOutputFormat } from "@anthropic-ai/sdk/helpers/zod";
import { z } from "zod/v4";
import { config } from "./config";

// KEEP IN SYNC with src/lib/claude.ts in the RN project — the Zod schema
// defines the shape of rows in the `plants` Supabase table and must not
// drift between the two clients. If you change this, update the RN copy
// in the same commit.

const client = new Anthropic({
  apiKey: config.ANTHROPIC_API_KEY,
  dangerouslyAllowBrowser: true,
});

const PlantAnalysisSchema = z.object({
  identified: z.boolean(),
  commonName: z.string(),
  scientificName: z.string(),
  plantType: z
    .string()
    .describe("e.g. succulent, tropical, flowering, herb, fern, cactus"),
  potSize: z
    .string()
    .describe("e.g. 'small (4-6 inch)', 'medium (8-10 inch)'"),
  plantSize: z.string().describe("e.g. seedling, young, mature, large"),
  healthStatus: z
    .string()
    .describe("healthy, mild issues, needs attention, critical"),
  diseases: z
    .array(z.string())
    .describe("Detected issues, pests, or diseases. Empty array if healthy"),
  careSuggestions: z
    .array(z.string())
    .describe("Actionable care tips specific to this plant's current condition"),
  wateringSchedule: z.object({
    frequencyDays: z.number(),
    amount: z.string().describe("light, moderate, or thorough"),
    notes: z.string().describe("Specific watering advice"),
  }),
  lightNeeds: z
    .string()
    .describe("low light, indirect bright, direct sun, etc."),
  needsMorePhotos: z.boolean(),
  photoRequests: z
    .array(z.string())
    .describe(
      "If needsMorePhotos is true, list specific photos needed for better diagnosis (e.g. close-up of leaves, soil surface, flowers)"
    ),
  confidence: z.number().describe("0.0 to 1.0"),
});

export type PlantAnalysis = z.infer<typeof PlantAnalysisSchema>;

const HealthCheckupSchema = z.object({
  healthStatus: z
    .string()
    .describe("healthy, mild issues, needs attention, critical"),
  diseases: z
    .array(z.string())
    .describe("Detected issues, pests, or diseases. Empty array if healthy"),
  careSuggestions: z
    .array(z.string())
    .describe("Actionable care tips specific to current condition"),
  comparisonNote: z
    .string()
    .describe(
      "1-2 concise bullet points comparing current health to previous state. " +
        "Use phrases like 'Improved: ...' or 'New issue: ...' or 'Unchanged: ...'. " +
        "If no previous data, say 'Initial checkup — no prior data to compare.'"
    ),
  confidence: z.number().describe("0.0 to 1.0"),
});

export type HealthCheckupResult = z.infer<typeof HealthCheckupSchema>;

const CHECKUP_PROMPT = `You are an expert botanist performing a health checkup on an existing plant. Analyze the NEW photo and compare the plant's current condition to its PREVIOUS health data provided below.

Focus exclusively on health assessment — do NOT re-identify the species. Provide updated health status, any diseases or issues visible, and actionable care suggestions.

For the comparison note, write 1-2 concise bullet points noting what changed (improved, worsened, new issues, resolved issues, or unchanged). Be specific — reference the actual conditions, not just generic statements like "health improved."`;

const ANALYSIS_PROMPT = `You are an expert botanist and plant care specialist. Analyze this plant image and provide a detailed assessment.

If you cannot identify the plant well from the image (damaged foliage, unclear photo, obscured species features), set identified to false, provide your best guess, set confidence low, and set needsMorePhotos to true with specific photo requests (e.g. close-up of leaves, flowers, soil surface, stem).

Be practical and specific with care suggestions. Consider the visible pot, soil condition, and plant health.`;

function manualNamePrompt(manualName: string): string {
  return `You are an expert botanist and plant care specialist. The user has told us this plant is "${manualName}". Trust this as the species identification — do NOT try to re-identify it from the image.

Focus your analysis entirely on this specific plant's current health: visible diseases, pests, damage, and tailored care advice.

Set identified to true. Use "${manualName}" (or its proper common name if slightly different) for commonName, and give the accurate scientificName for this species. Confidence should reflect your HEALTH assessment, not species identification.

Be practical and specific with care suggestions. Consider the visible pot, soil condition, and plant condition.`;
}

export async function analyzePlantImage(
  imageBase64: string,
  mimeType: string,
  manualName?: string,
  location?: string
): Promise<PlantAnalysis> {
  const locationContext = location
    ? `\n\nThe plant is located in ${location}. Factor in the local climate — typical temperature ranges, humidity levels, monsoon/dry seasons — when recommending watering frequency, amount, and care tips.`
    : "";

  const basePrompt = manualName ? manualNamePrompt(manualName) : ANALYSIS_PROMPT;
  const prompt = basePrompt + locationContext;

  const response = await client.messages.parse({
    model: "claude-sonnet-4-6",
    max_tokens: 4096,
    messages: [
      {
        role: "user",
        content: [
          {
            type: "image",
            source: {
              type: "base64",
              media_type: mimeType as
                | "image/jpeg"
                | "image/png"
                | "image/gif"
                | "image/webp",
              data: imageBase64,
            },
          },
          { type: "text", text: prompt },
        ],
      },
    ],
    output_config: {
      format: zodOutputFormat(PlantAnalysisSchema as unknown as never),
    },
  });

  if (!response.parsed_output) {
    throw new Error(
      `Claude failed to produce a structured response (stop_reason: ${response.stop_reason})`
    );
  }

  return response.parsed_output as PlantAnalysis;
}

export async function analyzeHealthCheckup(
  imageBase64: string,
  mimeType: string,
  plantName: string,
  previousHealth: {
    healthStatus: string;
    diseases: string[];
    careSuggestions: string[];
  }
): Promise<HealthCheckupResult> {
  const previousContext = `
Plant: ${plantName}
Previous health status: ${previousHealth.healthStatus}
Previous issues: ${previousHealth.diseases.length ? previousHealth.diseases.join("; ") : "None"}
Previous care suggestions: ${previousHealth.careSuggestions.join("; ")}`;

  const response = await client.messages.parse({
    model: "claude-sonnet-4-6",
    max_tokens: 4096,
    messages: [
      {
        role: "user",
        content: [
          {
            type: "image",
            source: {
              type: "base64",
              media_type: mimeType as "image/jpeg" | "image/png" | "image/gif" | "image/webp",
              data: imageBase64,
            },
          },
          { type: "text", text: CHECKUP_PROMPT + previousContext },
        ],
      },
    ],
    output_config: {
      format: zodOutputFormat(HealthCheckupSchema as unknown as never),
    },
  });

  if (!response.parsed_output) {
    throw new Error(
      `Claude failed to produce a checkup response (stop_reason: ${response.stop_reason})`
    );
  }

  return response.parsed_output as HealthCheckupResult;
}
